// Editorial-glass layout. The structural frame — no top bar; floating glass
// side-panel housing the brand mark + left-panel slot on the left; right-edge
// tool-rail slot; floating status-card slot bottom-right.
//
// On mobile (useIsMobile) the side panel + status card are dropped and the
// shared <MobileSheet> renders instead: a collapsible bottom sheet with
// Materials / Products tabs and a fixed status bar (count + cart). The brand
// mark and tool rail still float over the canvas, repositioned top-right. The
// mobile tab / status content comes from the mobile:* slots in the default
// mapping, so it's Designer-overridable independently of desktop.
//
// Content within the slots is composed from blocks via the default mapping in
// SingleMenuLayout.defaults.ts. Step 6 lets a Designer override the mapping
// per-deployment.

import { useMemo } from "react";
import { ConfiguratorViewer } from "../../../components/ConfiguratorViewer";
import { MeasurementOverlay } from "../components/MeasurementOverlay";
import { formatDecimal } from "../measurement";
import { Slot } from "../blocks/Slot";
import { mergeSlotMappings } from "../blocks/slotMapping";
import type { SlotMapping } from "../blocks/types";
import { MobileSheet, type MobileSheetTab } from "../components/MobileSheet";
import { useIsMobile } from "../useIsMobile";
import { useIsDraggingProduct } from "../useIsDraggingProduct";
import {
  SINGLE_MENU_DEFAULT_MAPPING,
  deriveMobileMaterialsFromLeftPanel,
  deriveMobileProductsFromLeftPanel,
} from "./SingleMenuLayout.defaults";
import type { LayoutProps } from "./types";

// Content tabs the mobile bottom sheet exposes. Each names a slot defined in
// the layout's default mapping (overridable per Designer). Brand product
// wording: "Materials" / "Products" rather than the desktop
// "Finishes" / "Components". The Cart tab is appended at render time (it's an
// action tab carrying the live item-count badge). `label` is the default
// caption; operators override each caption via a keyed text block in the
// mobile:menu-tabs slot (see tabCaption below). The tab id doubles as the
// caption key ("materials" / "products" / "cart").
const MOBILE_CONTENT_TABS: { id: string; label: string; slotId: string }[] = [
  { id: "materials", label: "Materials", slotId: "mobile:materials" },
  { id: "products", label: "Products", slotId: "mobile:products" },
];

/** Read a mobile tab caption from the dedicated `mobile:menu-tabs` slot — one
 *  text block per tab, keyed by tab id ("materials" / "products" / "cart").
 *  Falls back to `fallback` when absent / blank. The block's `text` is the
 *  server-resolved literal/translation, so captions are reworded + translated
 *  like any other text block. */
function tabCaption(mapping: SlotMapping, tabKey: string, fallback: string): string {
  const blk = (mapping["mobile:menu-tabs"] ?? []).find(b => b.block === "text" && b.key === tabKey);
  const text = blk && (blk.props as Record<string, unknown> | undefined)?.text;
  return typeof text === "string" && text.trim() ? text : fallback;
}

export function SingleMenuLayout(props: LayoutProps) {
  const {
    viewer,
    layoutFlags: layout,
    designerName,
    brandLogoUrl,
    selectedInstance,
    selectedProductName,
    selectedSeries,
    instances,
    viewerRef,
    viewerReady,
    setViewerReady,
    onUnityEvent,
    baseUrl,
    apiBaseUrl,
    blockHost,
    slotMappingOverride,
    openModal,
    onCloseSelection,
    measurementSetup,
    measurementsVisible,
  } = props;

  // Mobile vs. desktop chrome. Mobile renders <MobileSheet>; desktop renders
  // the static glass side panel below.
  const isMobile = useIsMobile();

  // On-canvas measurement labels honour the Designer's decimal separator (same
  // source the cart's measurement stat reads). "240.0 cm" vs "240,0 cm".
  const measurementFormat = useMemo(() => {
    const sep = blockHost.getFeature("decimalSeparator");
    const separator = sep === "," ? "," : ".";
    return (meters: number) => `${formatDecimal(meters * 100, 1, separator)} cm`;
  }, [blockHost]);

  // While a product is being dragged in, suppress the selection UI so it
  // doesn't pop up over the canvas mid-drag — it returns once the drag ends
  // (and the just-placed instance becomes the selection).
  const isDragging = useIsDraggingProduct();
  const selectionVisible =
    layout.showSelection && selectedInstance != null && !isDragging;
  // Merge the Designer's override over the layout default per-slot. Resolution
  // is per-slot, never per-block (.design.md "Axis 6 — Slot mapping").
  const mapping = useMemo(
    () => mergeSlotMappings(SINGLE_MENU_DEFAULT_MAPPING, slotMappingOverride),
    [slotMappingOverride]
  );

  // Mobile-tab content resolution. For each mobile tab slot we prefer, in order:
  //   1. an explicit Designer override of that mobile slot,
  //   2. content derived from the resolved left-panel — so the catalog's
  //      specParameterKeys (and the finishes labelling) carry over to mobile
  //      without the operator re-authoring them under the mobile slots,
  //   3. the static mobile default already sitting in `mapping`.
  // Only used on mobile; cheap, but memoised to keep the sheet's props stable.
  const mobileMapping = useMemo(() => {
    if (!isMobile) return mapping;
    const next = { ...mapping };
    const leftPanel = mapping["left-panel"];
    if (!slotMappingOverride?.["mobile:materials"]) {
      const derived = deriveMobileMaterialsFromLeftPanel(leftPanel);
      if (derived) next["mobile:materials"] = derived;
    }
    if (!slotMappingOverride?.["mobile:products"]) {
      const derived = deriveMobileProductsFromLeftPanel(leftPanel);
      if (derived) next["mobile:products"] = derived;
    }
    return next;
  }, [isMobile, mapping, slotMappingOverride]);

  // Mobile tabs = the content tabs plus a Cart action tab that opens the cart
  // modal and carries the live item-count badge. Gated on showCart so a
  // Designer that disables the cart drops the tab.
  const mobileTabs: MobileSheetTab[] = useMemo(() => {
    // Captions come from the mobile:menu-tabs slot, keyed by tab id, so all
    // three read + translate from one place; fall back to the built-in wording.
    const tabs: MobileSheetTab[] = MOBILE_CONTENT_TABS.map(t => ({
      ...t,
      label: tabCaption(mapping, t.id, t.label),
    }));
    if (layout.showCart) {
      tabs.push({
        id: "cart",
        label: tabCaption(mapping, "cart", "PDF"),
        onSelect: () => {
          // Surface the "empty cart" hint when opening with nothing placed.
          if (instances.length === 0) blockHost.notify?.("scene-empty-on-cart");
          openModal("cart");
        },
        badge: instances.length,
      });
    }
    return tabs;
  }, [layout.showCart, openModal, instances.length, blockHost, mapping]);

  return (
    <div className="sd__body">
      {/* Glass side panel — brand mark at top, left-panel slot fills below.
          Desktop only; at mobile widths the side panel + status card are
          replaced by the shared <MobileSheet> (rendered after the viewport). */}
      {layout.showCatalog && !isMobile && (
        <aside className="sd-eg-sidepanel">
          <div className="sd-eg-sidepanel__body">
            {/* Brand mark — top-left of the panel (DK3 redesign moves it here
                from the floating viewport corner). 32×32 at a 32px inset.
                Sits inside the scroll body in normal flow so it scrolls up
                with the rest of the panel content. */}
            {brandLogoUrl && (
              <div className="sd-eg-brand sd-eg-brand--panel">
                <img src={brandLogoUrl} alt={designerName ?? "Brand"} />
              </div>
            )}
            <Slot
              slotId="left-panel"
              mapping={mapping["left-panel"]}
              host={blockHost}
              className="sd-eg-leftpanel-slot"
            />
          </div>
        </aside>
      )}

      <div className="sd__viewport">
        {viewer ? (
          <ConfiguratorViewer
            ref={viewerRef}
            viewer={viewer}
            baseUrl={baseUrl}
            apiBaseUrl={apiBaseUrl}
            enableDragAndHotkeys
            onReady={() => setViewerReady(true)}
            onUnityEvent={onUnityEvent}
          />
        ) : (
          <div className="sd__viewport-placeholder">
            <div className="sd__viewport-icon">🛠</div>
            <p>No designer target configured.</p>
            <p className="sd__viewport-hint">
              Add <code>?target=&lt;designer-target&gt;</code> to the URL or pin one on the Designer.
            </p>
          </div>
        )}

        {/* Measurement lines drawn over the canvas. Only when measurements are
            toggled on; the overlay itself renders nothing when there's no setup
            payload (empty scene / lines off-screen). */}
        {viewer && viewerReady && measurementsVisible && (
          <MeasurementOverlay setup={measurementSetup} formatLength={measurementFormat} />
        )}

        {/* Right-edge vertical tool rail. The brand mark now lives in the
            left panel (see above), so the rail starts at the top of the
            right edge. The slot is rendered `inline` so this container is the
            only flex parent; ActionBlocks land directly as flex children. */}
        {layout.showSideMenu && viewer && viewerReady && (
          <div
            className="sd-eg-toolrail"
            role="toolbar"
            aria-label="Viewport actions"
          >
            <Slot
              slotId="tool-rail"
              mapping={mapping["tool-rail"]}
              host={blockHost}
              inline
            />
          </div>
        )}

        {/* Status card slot — bottom-right floating composite. Desktop only;
            on mobile the MobileSheet renders its own fixed status bar. The
            default mapping is a `group(stat, stat, group(action, action))`;
            layout CSS styles the group variants. */}
        {layout.showSummary && !isMobile && (
          <Slot
            slotId="status-card"
            mapping={mapping["status-card"]}
            host={blockHost}
            className="sd-eg-status-slot"
          />
        )}

        {/* Series name — top-left corner, heading font. Mobile only: it tells
            the user which series they're configuring now that the desktop side
            panel (which shows the series name in its header) is gone on mobile.
            On desktop the side panel already carries it, so we don't double up. */}
        {isMobile && selectedSeries?.label && (
          <div className="sd-eg-series-tag">{selectedSeries.label}</div>
        )}

        {/* On-selection popup — horizontal card slides up from the bottom
            when an instance is selected. Desktop only; on mobile the selection
            content takes over the bottom sheet instead. Slot-driven so the
            Designer can override the content (default: selected product name +
            the per-surface material picker). */}
        {selectionVisible && !isMobile && (
          <Slot
            slotId="selection-popup"
            mapping={mapping["selection-popup"]}
            host={blockHost}
            className="sd-eg-selection-card sd-eg-selection-card--open"
          />
        )}
      </div>

      {/* Mobile bottom sheet — replaces the side panel + status card at narrow
          widths. Tabs (Materials / Products) and the fixed status bar all read
          slots from the same mapping, so a Designer override customises mobile
          independently. Gated on showCatalog so a Designer that hides the
          catalog also hides the sheet. */}
      {isMobile && layout.showCatalog && (
        <MobileSheet
          tabs={mobileTabs}
          mapping={mobileMapping}
          host={blockHost}
          selection={
            selectionVisible
              ? { slotId: "selection-popup", title: selectedProductName, onClose: onCloseSelection }
              : null
          }
        />
      )}
    </div>
  );
}
